import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { useSnackbar } from '../contexts/SnackbarProvider';
import GlobalError from '../components/GlobalError';
import TableView from '../components/TableView';
import { formatCurrency } from '../utils/format';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import { Typography, Button, Link, Stack, IconButton, TextField, Box, Divider, Paper, Tooltip } from '@mui/material';
import { Remove, Add, Delete, ShoppingCartCheckout } from '@mui/icons-material';
const EcCartPage = () => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const cartItemsWithSubtotal = useMemo(() => {
        return cartItems.map(item => ({
            ...item,
            ...calculateItemSubtotal(item),
        }));
    }, [cartItems]);

    // 合計を計算
    const { total_amount, total_amount_including_tax } = useMemo(() => {
        return calculateTotalAmount(cartItemsWithSubtotal);
    }, [cartItemsWithSubtotal]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    // カートアイテムの取得
    const fetchCartItems = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/cart');
            setCartItems(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'データの取得に失敗しました');
            setCartItems([]);
        }
    }, []);
    useEffect(() => {
        fetchCartItems();
    }, [fetchCartItems]);
    // 商品がカートから削除された場合の処理
    const handleRemoveItem = async (itemId) => {
        const prevCart = [...cartItems];

        try {
            const res = await axiosInstance.delete(`/cart/remove/${itemId}`);
            setCartItems(res.data);
            showSnackbar(res?.data?.message || '商品をカートから削除しました', 'success');
        } catch (err) {
            console.error(err);
            setCartItems(prevCart);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };

    // 商品の数量を変更する関数
    const handleQuantityChange = async (itemId, variantId, quantity, minOrder) => {
        const parsedQuantity = Number(quantity);

        if (isNaN(parsedQuantity) || parsedQuantity < 0) {
            showSnackbar('数量は数字で入力してください', 'error');
            return;
        }

        if (parsedQuantity > 999999) {
            showSnackbar('数量は999,999以下で入力してください', 'error');
            return;
        }

        const prevCart = [...cartItems];

        if (parsedQuantity === 0) {
            const ok = window.confirm('数量を0にするとカートから削除されます。削除しますか？');
            if (!ok) return;
        } else if (minOrder && parsedQuantity < minOrder) {
            const ok = window.confirm(
                `この商品は最低注文数が ${minOrder} です。\nそれでも変更しますか？`
            );
            if (!ok) return;
        }
        // optimistic update
        setCartItems(prev =>
            prev.map(item =>
                item.cart_item_id === itemId
                    ? { ...item, quantity: parsedQuantity }
                    : item
            )
        );

        try {
            const res = await axiosInstance.put(`/cart/items/${itemId}`, {
                quantity: parsedQuantity,
            });
            setCartItems(res.data);
        } catch (err) {
            setCartItems(prevCart);
            alert(err.response?.data?.message || '更新に失敗しました');
        }
    };
    // カラム設定
    const columns = [
        {
            label: '商品',
            accessor: 'image_url',
            render: (item) => {
                return (
                    <img
                        alt={item.product_name || '商品画像'}
                        src={
                            item.image_url
                                ? `${process.env.REACT_APP_IMAGE_BASE_URL}${item.image_url.replace(/\\/g, '/')}`
                                : `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NOIMAGE}`
                        }
                        className="product-image"
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = `${process.env.REACT_APP_IMAGE_BASE_URL}${process.env.REACT_APP_NO_IMAGE}`;
                        }}
                    />
                );
            },
            width: '80px',
        },
        {
            accessor: 'sku',
            render: (item) => (
                <>
                    <Link component={RouterLink} to={`/products/${item.product_id}`} underline="hover">
                        {item.sku}<br />
                        {item.product_name +
                            (item.option_combination && Object.keys(item.option_combination).length > 0
                                ? ' / ' + Object.entries(item.option_combination)
                                    .map(([type, value]) => `(${type}) ${value}`)
                                    .join(' / ')
                                : ''
                            )
                        }
                    </Link>
                    <Typography>{formatCurrency(item.selling_price)}<Typography variant='caption'>
                        （税込 {formatCurrency(item.selling_price_including_tax)}）</Typography></Typography>
                </>
            ),
            minWidth: '250px',
        },
        {
            label: '数量',
            accessor: 'quantity',
            render: (item) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="減らす">
                        <IconButton
                            size="small"
                            onClick={() =>
                                handleQuantityChange(item.cart_item_id, item.variant_id, item.quantity - 1, item.min_order_quantity)
                            }
                        >
                            <Remove />
                        </IconButton>
                    </Tooltip>
                    <TextField
                        value={item.quantity}
                        onChange={(e) => {
                            let val = e.target.value.replace(/\D/g, ''); // 数字だけ

                            // 先頭ゼロ整理（任意）
                            if (val.length > 1) {
                                val = val.replace(/^0+/, '');
                            }

                            val = val.slice(0, 6); // 6桁制限

                            handleQuantityChange(
                                item.cart_item_id,
                                item.variant_id,
                                val,
                                item.min_order_quantity
                            );
                        }}
                        type="text"
                        size="small"
                        inputProps={{
                            inputMode: 'numeric',
                            style: { textAlign: 'center' },
                        }}
                    />
                    <Tooltip title="増やす">
                        <IconButton
                            size="small"
                            onClick={() =>
                                handleQuantityChange(item.cart_item_id, item.variant_id, item.quantity + 1)
                            }
                        >
                            <Add />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
            width: '200px',
        },
        {
            label: '金額',
            accessor: 'subtotal',
            render: (item) => {
                return (
                    <>
                        <Typography>
                            {formatCurrency(item.subtotal)}
                        </Typography>
                        <Typography variant='caption'>
                            税込 {formatCurrency(item.subtotal_including_tax)}
                        </Typography>
                    </>
                );
            },
            width: '200px',
        },
        {
            render: (item) => (
                <Tooltip title="削除">
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveItem(item.cart_item_id)}
                    >
                        <Delete />
                    </IconButton>
                </Tooltip>
            ),
            width: '30px',
        },

    ];

    // ローディングやエラー時の表示
    if (error) return <GlobalError errorMessage={error} />;

    return (
        <>
            <Typography variant="h1">カート</Typography>
            {/* カートアイテム一覧 */}
            <Paper variant='section'>
                <TableView dataset={cartItemsWithSubtotal} columns={columns} />
                {/* 合計金額 */}
                <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography fontWeight="bold">合計金額（税抜）</Typography>
                            <Typography fontWeight="bold">{formatCurrency(total_amount)}</Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography fontWeight="bold">合計金額（税込）</Typography>
                            <Typography fontSize={22} fontWeight="bold">{formatCurrency(total_amount_including_tax)}</Typography>
                        </Stack>
                        <Divider />
                    </Stack>
                </Box>
            </Paper>

            <Button
                variant="contained"
                color="primary"
                endIcon={<ShoppingCartCheckout />}
                onClick={() => navigate('/cart/checkout')}
                sx={{ mt: 1 }}
                disabled={cartItems.length === 0}
            >
                購入手続きへ進む
            </Button>
        </>
    );
};

export default EcCartPage;
